if (!customElements.get('gemid-popup')) {
  customElements.define(
    'gemid-popup',
    class GemidPopup extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('.gemid-popup__form');
        this.input = this.querySelector('input[name="gemid"]');
        this.errorElement = this.querySelector('.gemid-popup__error');
        this.submitButton = this.querySelector('.gemid-popup__submit');
        this.removeButton = this.querySelector('.gemid-popup__remove');
        this.allowCheckout = false;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.removeButton.addEventListener('click', this.onRemove.bind(this));

        document.addEventListener('click', this.onDocumentClick.bind(this), true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            setTimeout(() => this.check(), 0);
          });
        }

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          this.drawerObserver = new MutationObserver(() => {
            if (cartDrawer.classList.contains('active')) this.check();
          });
          this.drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
        }

        if (document.getElementById('main-cart-items')) this.check();
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.drawerObserver) this.drawerObserver.disconnect();
      }

      flagElements() {
        return Array.from(document.querySelectorAll('[data-gemid-required]'));
      }

      flagsSayRequired() {
        return this.flagElements().some((flag) => flag.dataset.gemidRequired === 'true');
      }

      taggedLineKeys() {
        const keys = new Set();
        this.flagElements().forEach((flag) => {
          (flag.dataset.gemidLineKeys || '')
            .split(',')
            .filter(Boolean)
            .forEach((key) => keys.add(key));
        });
        return keys;
      }

      // Tagged items are identified by the hidden `_use_gemid` line item
      // property (set by the add-to-cart forms) or by line keys collected
      // from the Liquid flags. Flags live in independently re-rendered
      // sections (drawer / main cart) and don't exist on product pages
      // (cart notification), so always confirm against the live cart.
      verifyRequired() {
        return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            if (cart.attributes && String(cart.attributes.gemid || '').trim() !== '') {
              this.matchedKeys = [];
              return false;
            }
            const flagKeys = this.taggedLineKeys();
            this.matchedKeys = cart.items
              .filter((item) => flagKeys.has(item.key) || (item.properties && item.properties._use_gemid))
              .map((item) => item.key);
            return this.matchedKeys.length > 0;
          })
          .catch(() => this.flagsSayRequired());
      }

      isCartVisible() {
        const cartDrawer = document.querySelector('cart-drawer');
        return (cartDrawer && cartDrawer.classList.contains('active')) || !!document.getElementById('main-cart-items');
      }

      check() {
        if (!this.flagsSayRequired()) {
          this.close();
          return;
        }
        if (!this.isCartVisible()) return;

        this.verifyRequired().then((required) => {
          required ? this.open() : this.close();
        });
      }

      open() {
        if (!this.hidden) return;
        this.hidden = false;
        document.body.classList.add('overflow-hidden');
        setTimeout(() => this.input.focus());
      }

      close() {
        if (this.hidden) return;
        this.hidden = true;
        this.showError('');

        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer || !cartDrawer.classList.contains('active')) {
          document.body.classList.remove('overflow-hidden');
        }
      }

      // Checkout buttons also appear outside flagged regions (e.g. the cart
      // notification on product pages), so every click is verified against
      // the live cart before letting the submit through.
      onDocumentClick(event) {
        if (this.allowCheckout) return;

        const checkoutButton = event.target.closest('[name="checkout"]');
        if (!checkoutButton) return;

        event.preventDefault();
        event.stopPropagation();

        this.verifyRequired().then((required) => {
          if (required) {
            this.open();
            return;
          }
          this.allowCheckout = true;
          checkoutButton.click();
          this.allowCheckout = false;
        });
      }

      onSubmit(event) {
        event.preventDefault();

        const value = this.input.value.trim();
        if (value === '') {
          this.showError(this.dataset.errorRequired);
          this.input.focus();
          return;
        }

        this.setLoading(this.submitButton, true);
        this.showError('');

        const body = JSON.stringify({ attributes: { gemid: value } });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.json();
          })
          .then(() => {
            this.flagElements().forEach((flag) => (flag.dataset.gemidRequired = 'false'));
            this.close();
          })
          .catch(() => {
            this.showError(this.dataset.errorSave);
          })
          .finally(() => {
            this.setLoading(this.submitButton, false);
          });
      }

      onRemove() {
        const keys =
          this.matchedKeys && this.matchedKeys.length > 0 ? this.matchedKeys : Array.from(this.taggedLineKeys());
        if (keys.length === 0) {
          this.close();
          return;
        }

        this.setLoading(this.removeButton, true);
        this.showError('');

        keys
          .reduce(
            (promise, key) =>
              promise.then(() => {
                const body = JSON.stringify({ id: key, quantity: 0 });
                return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
              }),
            Promise.resolve()
          )
          .then(() => {
            window.location.reload();
          })
          .catch(() => {
            this.showError(this.dataset.errorRemove);
            this.setLoading(this.removeButton, false);
          });
      }

      showError(message) {
        this.errorElement.textContent = message || '';
        this.errorElement.hidden = !message;
      }

      setLoading(button, isLoading) {
        button.toggleAttribute('disabled', isLoading);
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }
    }
  );
}
